import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { EditorView } from "@tiptap/pm/view";

const EXPLICIT_URL_PATTERN = /^(https?:\/\/|www\.)\S+$/i;

/**
 * True when the text is a single explicit URL token — one starting with
 * http(s):// or www. — the signal that the author meant it as a link.
 * Bare "word.word" text (as.it, google.com) never counts, so an accidental
 * missing space after a period can't linkify. Also used as the Link
 * extension's shouldAutoLink filter.
 */
export const isExplicitUrl = (text: string): boolean => EXPLICIT_URL_PATTERN.test(text.trim());

/** Convert an explicit URL token into an href, or null when it isn't one. */
export const explicitUrlToHref = (text: string): string | null => {
  const trimmed = text.trim();
  if (!isExplicitUrl(trimmed)) {
    return null;
  }
  const candidate = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    return new URL(candidate).hostname ? candidate : null;
  } catch {
    return null;
  }
};

export const handleUrlPaste = (view: EditorView, event: ClipboardEvent): boolean => {
  const { state } = view;
  if (!state.selection.empty) {
    return false;
  }
  const linkMark = state.schema.marks.link;
  if (!linkMark) {
    return false;
  }
  const text = event.clipboardData?.getData("text/plain")?.trim() ?? "";
  const href = explicitUrlToHref(text);
  if (!href) {
    return false;
  }
  const node = state.schema.text(text, [linkMark.create({ href })]);
  view.dispatch(state.tr.replaceSelectionWith(node, false).scrollIntoView());
  return true;
};

/**
 * Links a URL pasted at a caret. The Link extension only covers the other two
 * paths: linkOnPaste needs a non-empty selection, and autolink needs typed
 * trailing whitespace — so a URL pasted into empty space stayed plain text.
 */
export const PasteUrlAsLink = Extension.create({
  name: "pasteUrlAsLink",

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey("pasteUrlAsLink"),
        props: {
          handlePaste: handleUrlPaste,
        },
      }),
    ];
  },
});
